import { useState, useEffect, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '../components/LanguageSelector';
import { APP_VERSION, BUILD_DATE } from '../lib/version';

interface LoginProps {
  onLogin: (email: string, password: string) => Promise<void>;
  onBiometricLogin?: () => Promise<void>;
  onEnableBiometric?: () => Promise<void>;
  onDismissBiometricPrompt?: () => void;
  showBiometricPrompt?: boolean;
  biometricAvailable?: boolean;
  biometricEnabled?: boolean;
  biometricType?: string;
  error: string | null;
  loading: boolean;
}

export function Login({
  onLogin, onBiometricLogin, onEnableBiometric, onDismissBiometricPrompt,
  showBiometricPrompt, biometricAvailable, biometricEnabled, biometricType,
  error, loading,
}: LoginProps) {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (biometricEnabled && onBiometricLogin) {
      onBiometricLogin();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await onLogin(email, password).catch(() => {});
  };

  const biometricIcon = biometricType === 'face' ? '😊' : '🔒';
  const biometricLabel = biometricType === 'face' ? t('face_id') : t('fingerprint');

  return (
    <div className="login-page">
      {/* Language selector — fixed top right */}
      <div className="login-lang-fixed">
        <LanguageSelector compact />
      </div>

      <div className="login-card">
        {/* Banner image */}
        <div className="login-banner">
          <img src="/images/welcome-banner-sm.png" alt="Welcome to Bus Tracker" />
        </div>

        {/* App title */}
        <div className="login-title-section">
          <h1 className="login-app-title">School Bus Tracker</h1>
          <p className="login-tagline">Real-time tracking for safer school commutes</p>
        </div>

        {/* Verified banner */}
        {typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('verified') === 'true' && (
          <div style={{
            background: 'rgba(74, 222, 128, 0.1)',
            border: '1px solid rgba(74, 222, 128, 0.3)',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            textAlign: 'center',
            color: '#4ade80',
            fontSize: '14px',
            fontWeight: 600,
          }}>
            Email verified! You can now log in.
          </div>
        )}

        {/* Login form */}
        <form onSubmit={handleSubmit} aria-label="Login form">
          <div className="form-group login-input-group">
            <span className="login-input-icon">@</span>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('email_placeholder')}
              required
              autoComplete="email"
              className="login-input"
              aria-label="Email address"
            />
          </div>
          <div className="form-group login-input-group">
            <span className="login-input-icon">*</span>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('password_placeholder')}
              required
              autoComplete="current-password"
              className="login-input"
              aria-label="Password"
            />
          </div>
          {error && <div className="form-error">{error}</div>}
          <button type="submit" className="btn-login" disabled={loading}>
            {loading ? t('logging_in') : t('login_button')}
          </button>
        </form>

        <div style={{ textAlign: 'center', margin: '1rem 0' }}>
          <a href="/signup" style={{ color: '#e94560', textDecoration: 'none', fontSize: '0.9rem' }}>
            New school? Register for free &rarr;
          </a>
        </div>

        {biometricEnabled && onBiometricLogin && (
          <>
            <div className="biometric-divider"><span>{t('or', { ns: 'common' })}</span></div>
            <button className="btn-biometric" onClick={onBiometricLogin} disabled={loading}>
              <span className="biometric-icon">{biometricIcon}</span>
              <span>{t('use_biometric', { type: biometricLabel })}</span>
            </button>
          </>
        )}

        {/* Quick login */}
        <div className="dev-quick-login">
          <p className="dev-label">{t('quick_login_dev')}</p>
          <div className="dev-buttons">
            <button className="dev-btn dev-btn-parent" onClick={() => onLogin('parent@school.com', 'Parent@2026')} disabled={loading}>
              {t('parent')}
            </button>
            <button className="dev-btn dev-btn-driver" onClick={() => onLogin('driver@school.com', 'Driver@2026')} disabled={loading}>
              {t('driver')}
            </button>
            <button className="dev-btn dev-btn-admin" onClick={() => onLogin('admin@school.com', 'Admin@2026')} disabled={loading}>
              {t('admin')}
            </button>
            <button className="dev-btn dev-btn-super" onClick={() => onLogin('admin@school.com', 'Admin@2026')} disabled={loading}>
              Super Admin
            </button>
          </div>
        </div>

        <div className="version-footer">{APP_VERSION}</div>
      </div>

      {showBiometricPrompt && (
        <div className="biometric-onboard-overlay">
          <div className="biometric-onboard-card">
            <div className="biometric-visual">
              {biometricType === 'face' ? (
                <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="8" y="8" width="48" height="48" rx="16" />
                  <circle cx="24" cy="26" r="2.5" fill="currentColor" stroke="none" />
                  <circle cx="40" cy="26" r="2.5" fill="currentColor" stroke="none" />
                  <path d="M24 40c4 4 12 4 16 0" strokeWidth="2.5" />
                  <path d="M8 18V12a4 4 0 0 1 4-4h6" opacity="0.4" />
                  <path d="M46 8h6a4 4 0 0 1 4 4v6" opacity="0.4" />
                  <path d="M56 46v6a4 4 0 0 1-4 4h-6" opacity="0.4" />
                  <path d="M18 56h-6a4 4 0 0 1-4-4v-6" opacity="0.4" />
                </svg>
              ) : (
                <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M32 6c-6 0-12 2-16 6" opacity="0.3" />
                  <path d="M20 16c-2 3-4 7-4 12" opacity="0.4" />
                  <path d="M32 6c4 0 8 1.5 11 4" opacity="0.35" />
                  <path d="M48 16c2 4 2.5 7 2.5 12" opacity="0.45" />
                  <path d="M32 10c-4 0-8 2-10.5 5.5" opacity="0.5" />
                  <path d="M24 20c-1.5 2.5-2 5-2 9v6" opacity="0.6" />
                  <path d="M32 10c5 0 9 3 11 7" opacity="0.55" />
                  <path d="M46 22c0.5 2 0.5 4 0.5 6" opacity="0.65" />
                  <path d="M32 14c-3 0-6 2-7.5 5" strokeWidth="2.5" />
                  <path d="M27 24c-0.5 2-0.5 4 0 7v8c0 4 2 8 5 11" strokeWidth="2.5" />
                  <path d="M32 14c4 0 7 3 8 6.5" strokeWidth="2.5" />
                  <path d="M42 26v4c0 3-0.5 6-2 9" strokeWidth="2.5" />
                  <path d="M32 20v4c0 5 0 10-1 14" strokeWidth="2.5" />
                  <path d="M36 28c0 4-0.5 8-2 12" strokeWidth="2" opacity="0.8" />
                </svg>
              )}
              <div className="biometric-scan-line" />
            </div>
            <h2>Secure Your Login</h2>
            <p className="biometric-onboard-desc">
              Use your {biometricLabel.toLowerCase()} for instant, password-free access. Your biometric data never leaves your device.
            </p>
            <ul className="biometric-benefits">
              <li>Sign in instantly without typing</li>
              <li>Your biometric data stays on-device</li>
              <li>Works even when you're offline</li>
            </ul>
            <button className="btn-primary" onClick={onEnableBiometric}>
              Enable {biometricLabel}
            </button>
            <button className="btn-ghost" onClick={onDismissBiometricPrompt}>Not now</button>
          </div>
        </div>
      )}
    </div>
  );
}
